import React, { useEffect, useMemo } from 'react';
import { Card, Tab } from 'react-bootstrap';

import { usePokCore } from '../../../common/hooks/usePokCore';
import { TabsHistory } from '../../../app/components/TabsHistory';
import { useTranslation } from '../../../common/hooks/useTranslation';
import { EODLogSearch } from '../../components/EOD/EODLogSearch';
import { EodImportLogSearch } from '../../components/EOD/EodImportLogSearch';
import {
  GridCountFunction,
  GridGetterFunction,
} from '../../../common/components/Grid/GridDataTypes';
import { TurnedOffInCompany } from '../../../common/components/NotAuthorized/TurnedOffInCompany';
import { GetCompanyDto } from '../../../common/pokCore/autogenerated/pokApiClient';
import { JobConfigInfo } from '../../components/JobConfig/JobConfigInfo';
import { JobTypeEnum } from '../../../common/pokCore/validation/enums';

export const EODErrorsView: React.FC = () => {
  const pok = usePokCore();
  const { t, tk } = useTranslation('fvzLng');
  const [refresh, setRefresh] = React.useState(false);
  const [refreshErrors, setRefreshErrors] = React.useState(false);
  const [company, setCompany] = React.useState<GetCompanyDto | undefined>(
    undefined,
  );

  useEffect(() => {
    pok.companies
      .getById(pok.getCompanyId())
      .then(companyData => {
        setCompany(companyData);
      })
      .catch(() => {
        setCompany(undefined);
      });
  }, [pok]);

  const getAllImportLogData = useMemo(
    () =>
      ((pageSize, pageNumber, orderBy, orderDirection, filterText) =>
        pok.eodImportLogs.getAllByPortion(
          t,
          tk,
          pok.getCompanyId(),
          refresh,
          setRefresh,
          pageSize,
          pageNumber,
          orderBy,
          orderDirection,
          filterText,
        )) as GridGetterFunction,
    [pok, t, tk, refresh],
  );

  const getAllImportLogCount = useMemo(
    () =>
      (filterText =>
        pok.eodImportLogs.getAllCount(
          pok.getCompanyId(),
          filterText,
        )) as GridCountFunction,
    [pok],
  );

  const getAllEodLogData = useMemo(
    () =>
      ((pageSize, pageNumber, orderBy, orderDirection, filterText) =>
        pok.eodLogs.getAllByPortion(
          t,
          tk,
          refreshErrors,
          setRefreshErrors,
          pageSize,
          pageNumber,
          orderBy,
          orderDirection,
          filterText,
        )) as GridGetterFunction,
    [pok, t, tk, refreshErrors],
  );

  if (company && !company?.eod) {
    return (
      <TurnedOffInCompany
        heading={t(tk.prLang.eodInCompany)}
        longText={t(tk.prLang.eodNotInCompanyLongText, {
          companyName: company?.name,
        })}
      />
    );
  }

  return (
    <Card className="main-card">
      <Card.Body className="m-3">
        <Card.Title className="pb-3">{t(tk.eod.errorsEOD)}</Card.Title>
        <TabsHistory
          activeKey="importLogsEOD"
          id="errorsEODTabs"
          className="mb-3"
          mountOnEnter
          unmountOnExit
        >
          <Tab
            eventKey="importLogsEOD"
            title={t(tk.eod.allImportLogsEOD)}
            className="m-3"
          >
            <EodImportLogSearch
              getData={getAllImportLogData}
              getCount={getAllImportLogCount}
              refresh={refresh}
            />
          </Tab>
          <Tab
            eventKey="errorsEOD"
            title={t(tk.eod.errorsFromEOD)}
            className="m-3"
          >
            <EODLogSearch
              getData={getAllEodLogData}
              getCount={pok.eodLogs.getAllCount}
              refresh={refreshErrors}
            />
          </Tab>
        </TabsHistory>
        <JobConfigInfo
          items={[
            {
              jobType: JobTypeEnum.EodDocuments,
              nextStartLabel: t(tk.prLang.eodNextStart),
              frequencyLabel: t(tk.prLang.frequency),
            },
          ]}
        />
      </Card.Body>
    </Card>
  );
};
